import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { blockIpAddress, fetchActivitySessions, unblockIpAddress } from '../api/auth';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './AdminActivityPage.module.css';

const formatDateValue = (value) => {
  if (!value) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function AdminActivityPage({ currentUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [blockingIp, setBlockingIp] = useState('');
  const [selectedActivitySession, setSelectedActivitySession] = useState(null);
  const [hideAdmins, setHideAdmins] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    username: searchParams.get('username') || '',
    role: '',
    status: '',
    startDate: null,
    endDate: null,
  });

  const visibleActivity = useMemo(
    () => (hideAdmins ? activity.filter((row) => row.role !== 'admin') : activity),
    [activity, hideAdmins]
  );

  const activitySummary = useMemo(() => ({
    total: visibleActivity.length,
    connected: visibleActivity.filter((row) => !row.disconnectedAt).length,
    disconnected: visibleActivity.filter((row) => Boolean(row.disconnectedAt)).length,
  }), [visibleActivity]);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchActivitySessions({
        limit: 500,
        search: filters.search,
        username: filters.username,
        role: filters.role,
        status: filters.status,
        startDate: formatDateValue(filters.startDate),
        endDate: formatDateValue(filters.endDate),
      });
      setActivity(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (err) {
      setError(err.message || t('Unable to load activity sessions.'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadActivity();
  }, [currentUser?.role, loadActivity, navigate]);

  useEffect(() => {
    const usernameFromQuery = searchParams.get('username') || '';
    setFilters((current) => (
      current.username === usernameFromQuery
        ? current
        : { ...current, username: usernameFromQuery }
    ));
  }, [searchParams]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: '',
      username: searchParams.get('username') || '',
      role: '',
      status: '',
      startDate: null,
      endDate: null,
    });
  };

  const handleToggleIpBlock = async (row) => {
    if (!row.ipAddress) return;

    const isBlocking = !row.isBlocked;
    const confirmed = window.confirm(
      isBlocking
        ? t('Block IP "{{ip}}" from accessing the app?', { ip: row.ipAddress })
        : t('Unblock IP "{{ip}}"?', { ip: row.ipAddress })
    );
    if (!confirmed) return;

    setBlockingIp(row.ipAddress);
    setMessage('');
    setError('');
    try {
      if (isBlocking) {
        await blockIpAddress(row.ipAddress);
        setMessage(t('Blocked IP {{ip}}.', { ip: row.ipAddress }));
      } else {
        await unblockIpAddress(row.ipAddress);
        setMessage(t('Unblocked IP {{ip}}.', { ip: row.ipAddress }));
      }
      await loadActivity();
    } catch (err) {
      setError(err.message || t('Unable to change IP block status.'));
    } finally {
      setBlockingIp('');
    }
  };

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <main className={styles.container}>
      <GoToDashboardButton />

      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t('Administration')}</p>
          <h1 className={styles.title}>{t('Access Activity')}</h1>
          <p className={styles.subtitle}>
            {t('Review every access session with filters for username, time range, status, role, and free text details such as IP, location, browser, and visited path.')}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin')}>
            {t('Back to Admin Panel')}
          </button>
          <button type="button" className={styles.primaryButton} onClick={loadActivity} disabled={loading}>
            {loading ? t('Loading...') : t('Refresh activity')}
          </button>
        </div>
      </header>

      <section className={styles.metricsGrid}>
        <Metric label={t('Sessions shown')} value={activitySummary.total} />
        <Metric label={t('Still connected')} value={activitySummary.connected} />
        <Metric label={t('Disconnected')} value={activitySummary.disconnected} />
      </section>

      {(message || error) && (
        <div className={`${styles.notice} ${error ? styles.error : styles.success}`}>
          {error || message}
        </div>
      )}

      <section className={styles.filtersPanel}>
        <div className={styles.filtersGrid}>
          <input
            className={styles.filterInput}
            value={filters.search}
            onChange={(event) => handleFilterChange('search', event.target.value)}
            placeholder={t('Search IP, location, browser, path, or anything else')}
          />
          <input
            className={styles.filterInput}
            value={filters.username}
            onChange={(event) => handleFilterChange('username', event.target.value)}
            placeholder={t('Filter by username')}
          />
          <select
            className={styles.filterInput}
            value={filters.role}
            onChange={(event) => handleFilterChange('role', event.target.value)}
          >
            <option value="">{t('All roles')}</option>
            <option value="admin">{t('Admin')}</option>
            <option value="normal">{t('Normal user')}</option>
          </select>
          <select
            className={styles.filterInput}
            value={filters.status}
            onChange={(event) => handleFilterChange('status', event.target.value)}
          >
            <option value="">{t('All statuses')}</option>
            <option value="connected">{t('Still connected')}</option>
            <option value="disconnected">{t('Disconnected')}</option>
          </select>
          <DatePicker
            selected={filters.startDate}
            onChange={(value) => handleFilterChange('startDate', value)}
            dateFormat="dd/MM/yyyy"
            placeholderText={t('Connected from date')}
            className={styles.filterInput}
            isClearable
          />
          <DatePicker
            selected={filters.endDate}
            onChange={(value) => handleFilterChange('endDate', value)}
            dateFormat="dd/MM/yyyy"
            placeholderText={t('Connected to date')}
            className={styles.filterInput}
            isClearable
          />
        </div>
        <div className={styles.filterActions}>
          <button type="button" className={styles.primaryButton} onClick={loadActivity}>
            {t('Apply Filters')}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleReset}>
            {t('Reset Filters')}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setHideAdmins((current) => !current)}
          >
            {hideAdmins ? t('Show admins activity') : t('Hide admins activity')}
          </button>
        </div>
      </section>

      <section className={styles.panel}>
        {loading ? (
          <div className={styles.emptyState}>{t('Loading activity sessions...')}</div>
        ) : visibleActivity.length === 0 ? (
          <div className={styles.emptyState}>{t('No activity sessions recorded yet.')}</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.activityTable}>
              <thead>
                <tr>
                  <th>{t('User')}</th>
                  <th>{t('Role')}</th>
                  <th>{t('IP')}</th>
                  <th>{t('Location')}</th>
                  <th>{t('Connected')}</th>
                  <th>{t('Last seen')}</th>
                  <th>{t('Disconnected')}</th>
                  <th>{t('Last page')}</th>
                  <th>{t('Pages visited')}</th>
                  <th>{t('Environment')}</th>
                  <th>{t('IP control')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleActivity.map((row) => (
                  <tr key={row.id}>
                    <td><strong>{row.username}</strong></td>
                    <td>{t(roleLabel(row.role))}</td>
                    <td className={styles.monoCell}>{row.ipAddress || '-'}</td>
                    <td>{row.location || row.country || row.timezone || t('Unknown')}</td>
                    <td>{formatDate(row.connectedAt, i18n.language)}</td>
                    <td>{formatDate(row.lastSeenAt, i18n.language)}</td>
                    <td>{formatDate(row.disconnectedAt, i18n.language)}</td>
                    <td className={styles.monoCell}>{row.lastPath || '-'}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.visitCountButton}
                        onClick={() => setSelectedActivitySession(row)}
                      >
                        <span className={styles.visitCount}>{row.pageVisitCount || 0}</span>
                        <span>{t('View pages')}</span>
                      </button>
                    </td>
                    <td>
                      <div className={styles.environmentCell}>
                        <span>{[row.browser, row.os, row.device].filter(Boolean).join(' · ') || '-'}</span>
                        {row.disconnectReason ? <em>{t('Disconnect reason')}: {row.disconnectReason}</em> : null}
                      </div>
                    </td>
                    <td>
                      <div className={styles.ipControlCell}>
                        <span className={`${styles.statusBadge} ${row.isBlocked ? styles.blockedBadge : styles.allowedBadge}`}>
                          {row.isBlocked ? t('Blocked') : t('Allowed')}
                        </span>
                        <button
                          type="button"
                          className={row.isBlocked ? styles.secondaryButton : styles.dangerButton}
                          onClick={() => handleToggleIpBlock(row)}
                          disabled={!row.ipAddress || blockingIp === row.ipAddress}
                        >
                          {blockingIp === row.ipAddress
                            ? t('Saving...')
                            : row.isBlocked
                              ? t('Unblock IP')
                              : t('Block IP')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedActivitySession ? (
        <div className={styles.modalBackdrop} onClick={() => setSelectedActivitySession(null)}>
          <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>{t('Visited pages')}</p>
                <h3>{selectedActivitySession.username}</h3>
                <p>
                  {t('{{count}} total page visit(s)', {
                    count: selectedActivitySession.pageVisitCount || 0,
                  })}
                </p>
              </div>
              <button type="button" className={styles.secondaryButton} onClick={() => setSelectedActivitySession(null)}>
                {t('Close')}
              </button>
            </div>

            {selectedActivitySession.pageVisits?.length ? (
              <div className={styles.modalVisitList}>
                {selectedActivitySession.pageVisits.map((visit, index) => (
                  <div key={`${visit.visitedAt}-${visit.path}-${index}`} className={styles.modalVisitRow}>
                    <strong className={styles.monoCell}>{visit.path}</strong>
                    <span>{formatDate(visit.visitedAt, i18n.language)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>{t('No visited pages recorded for this session.')}</div>
            )}
          </div>
        </div>
      ) : null}
    </main>
  );
}

function Metric({ label, value }) {
  return (
    <div className={styles.metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function roleLabel(role) {
  return role === 'admin' ? 'Admin' : 'Normal user';
}

function formatDate(value, language) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(language === 'it' ? 'it-IT' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}
